import { ReactNode } from 'react';
import { CSSObject } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ButtonBar, Modal, ModalControlModalProps, Text } from '@frontend/design-system';

type Props = ModalControlModalProps & {
  bodyStyles?: CSSObject;
  children?: ReactNode;
  title: string;
  trackingIdPrefix: string;
};

export const BulkMessagingModal = ({ bodyStyles, children, title, trackingIdPrefix, ...rest }: Props) => (
  <Modal
    css={{
      minWidth: 600,
    }}
    {...rest}
  >
    <Modal.Header
      css={{
        marginBottom: theme.spacing(0),
      }}
    >
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: theme.spacing(2),
        }}
      >
        <Text
          css={{
            fontSize: theme.fontSize(24),
            textAlign: 'left',
          }}
        >
          {title}
        </Text>
        <Button
          size={'large'}
          variant='secondary'
          iconName='x'
          onClick={rest.onClose}
          trackingId={`${trackingIdPrefix}-close-btn`}
        />
      </div>
    </Modal.Header>
    <Modal.Body
      css={[
        {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
        bodyStyles,
      ]}
    >
      {children}
    </Modal.Body>
  </Modal>
);

type ButtonBarProps = {
  onAction: () => void;
  onCancel: () => void;
  actionText?: string;
  cancelText?: string;
  trackingId?: string;
};

const BulkEmailModalButtonBar = ({ onAction, onCancel, cancelText, actionText, trackingId }: ButtonBarProps) => {
  const { t } = useTranslation('bulk-messaging');
  const cancelLabel = cancelText ?? t('Cancel');
  const actionLabel = actionText ?? t('Confirm');

  return (
    <ButtonBar
      css={{
        marginTop: theme.spacing(2),
      }}
    >
      <Button
        css={bottomBtn}
        onClick={onCancel}
        size='small'
        trackingId={`${trackingId}-cancel-btn`}
        variant='secondary'
      >
        {cancelLabel}
      </Button>
      <Button size='small' css={[bottomBtn, actionBtn]} onClick={onAction} trackingId={`${trackingId}-action-btn`}>
        {actionLabel}
      </Button>
    </ButtonBar>
  );
};

BulkEmailModalButtonBar.displayName = 'BulkEmailModalButtonBar';
BulkMessagingModal.ButtonBar = BulkEmailModalButtonBar;

const bottomBtn: CSSObject = {
  maxWidth: 'fit-content',
};

const actionBtn: CSSObject = {
  background: theme.colors.critical50,
  borderColor: theme.colors.critical60,

  '&:hover': {
    background: theme.colors.critical40,
    borderColor: theme.colors.critical50,
  },
};

type ContainerProps = {
  children?: ReactNode;
};

const BulkEmailModalTextContainer = ({ children }: ContainerProps) => {
  return (
    <div
      css={{
        padding: theme.spacing(0, 3),
      }}
    >
      {children}
    </div>
  );
};

BulkEmailModalTextContainer.displayName = 'BulkEmailModalTextContainer';
BulkMessagingModal.TextContainer = BulkEmailModalTextContainer;

const BulkEmailModalRecipientsContainer = ({ children }: ContainerProps) => {
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 340,
        overflowY: 'auto',
      }}
    >
      {children}
    </div>
  );
};

BulkEmailModalRecipientsContainer.displayName = 'BulkEmailModalRecipientsContainer';
BulkMessagingModal.RecipientsContainer = BulkEmailModalRecipientsContainer;

const BulkEmailModalButtonContainer = ({ children }: ContainerProps) => {
  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {children}
    </div>
  );
};

BulkEmailModalButtonContainer.displayName = 'BulkEmailModalButtonContainer';
BulkMessagingModal.ButtonContainer = BulkEmailModalButtonContainer;
